// React i18n state layer, shared by the admin app and the public spectator
// view. The active locale is resolved from layered sources, first match wins:
//
//   1. the user's explicit selection (persisted in localStorage),
//   2. the signed-in user's company default language (admin app only — synced
//      from the server data via `setCompanyLocale`, never persisted),
//   3. the device locale (`navigator.language`),
//   4. English.
//
// The public view never sets a company locale, so a spectator gets their
// device's language; a company user sees their company's chosen language until
// they explicitly pick another. `<html lang/dir>` is kept in sync for
// accessibility and correct rendering.
//
// Components read it via `useTranslation`. The context ships with a working
// default (English) so components render correctly even outside a provider —
// e.g. in unit tests that mount a view directly.

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import {
  DEFAULT_LOCALE,
  LOCALE_META,
  toLocale,
  translate,
  type Locale,
  type TranslateParams,
} from '../i18n/messages';

export interface I18nContextValue {
  locale: Locale;
  /**
   * Explicitly select a language; persisted, overrides every other source.
   * Passing null drops the explicit choice, so resolution falls back to the
   * company default / device locale — what "follow my company" means in the
   * account settings (see `features/user-settings.md`).
   */
  setLocale: (locale: Locale | null) => void;
  /**
   * Set (or clear with null) the company-default layer. The admin app syncs
   * this from the signed-in user's company; it applies only while the user has
   * not made an explicit selection, and it is never persisted.
   */
  setCompanyLocale: (locale: Locale | null) => void;
  /** Translate a key, interpolating params and handling plurals via `count`. */
  t: (key: string, params?: TranslateParams) => string;
  /** BCP-47 code for `Intl` date/number formatters in the active locale. */
  localeCode: string;
}

const STORAGE_KEY = 'tae.locale';

/** The user's persisted explicit choice, or null when they never made one. */
function storedLocale(): Locale | null {
  try {
    return toLocale(localStorage.getItem(STORAGE_KEY));
  } catch {
    // localStorage may be unavailable (private mode / SSR).
    return null;
  }
}

/** The device language, when it is one the platform supports. */
function deviceLocale(): Locale | null {
  if (typeof navigator === 'undefined') return null;
  return toLocale(navigator.language);
}

const I18nContext = createContext<I18nContextValue>({
  locale: DEFAULT_LOCALE,
  setLocale: () => {},
  setCompanyLocale: () => {},
  t: (key, params) => translate(DEFAULT_LOCALE, key, params),
  localeCode: LOCALE_META[DEFAULT_LOCALE].code,
});

export function I18nProvider({ children }: { children: ReactNode }) {
  const [chosen, setChosen] = useState<Locale | null>(storedLocale);
  const [companyLocale, setCompanyLocale] = useState<Locale | null>(null);

  const locale = chosen ?? companyLocale ?? deviceLocale() ?? DEFAULT_LOCALE;

  useEffect(() => {
    const meta = LOCALE_META[locale];
    document.documentElement.lang = meta.code;
    document.documentElement.dir = meta.dir;
  }, [locale]);

  const setLocale = useCallback((next: Locale | null) => {
    setChosen(next);
    try {
      if (next === null) localStorage.removeItem(STORAGE_KEY);
      else localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // Persisting is best-effort; the in-memory selection still applies.
    }
  }, []);

  const value = useMemo<I18nContextValue>(
    () => ({
      locale,
      setLocale,
      setCompanyLocale,
      localeCode: LOCALE_META[locale].code,
      t: (key, params) => translate(locale, key, params),
    }),
    [locale, setLocale],
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

/** Access the active locale and the `t` translator. */
export function useTranslation(): I18nContextValue {
  return useContext(I18nContext);
}
